import { Getter } from '../../Getter';
import { HttpError } from '../../HttpError';
import isValidUUID from 'uuid-validate';
import { ApiCommentCounts, CommentCounts, CommentCountInput } from './Models';
import URI from 'urijs';

type ApiResponse = ApiCommentCounts | null | undefined;

interface DataResponse {
  data?: CommentCounts;
  error?: never;
}

interface ErrorResponse {
  data?: never;
  error?: HttpError;
}

export type ClientOutput = DataResponse | ErrorResponse;

export class CommentCountClient {
  private getter: Getter<ApiResponse | HttpError>;

  constructor(getter: Getter<ApiResponse | HttpError>) {
    this.getter = getter;
  }

  public async retrieveAsync(input: CommentCountInput): Promise<ClientOutput> {
    const { organizationUUID, repositoryUUID } = input;

    if (!isValidUUID(organizationUUID)) {
      return {
        error: new HttpError(400),
      };
    }

    if (repositoryUUID !== undefined && !isValidUUID(repositoryUUID)) {
      return {
        error: new HttpError(400),
      };
    }

    const response = await this.tryRetrieveAsync(input);

    if (!response) {
      return {
        data: undefined,
        error: undefined,
      };
    }

    if (response instanceof HttpError) {
      return {
        error: response,
      };
    }

    return {
      data: this.toCommentCounts(response),
    };
  }

  private async tryRetrieveAsync(
    input: CommentCountInput
  ): Promise<ApiResponse | HttpError> {
    const href = this.getHref(input);

    return await this.getter.get(href);
  }

  private toCommentCounts(
    response: ApiCommentCounts
  ): CommentCounts | undefined {
    return {
      inlineCommentCount: response.inline,
      inlineReplyCommentCount: response.inline_reply,
      summaryCommentCount: response.summary,
      summaryReplyCommentCount: response.summary_reply,
      totalCommentCount: response.total_comments_count,
      codeReviewCount: response.code_reviews_count,
    };
  }

  public getHref({
    organizationUUID,
    repositoryUUID,
    dateRange,
  }: CommentCountInput): string {
    const uri = URI('/api/v1/organizations')
      .segmentCoded(organizationUUID)
      .segment('comment-stream')
      .segment('count');

    if (repositoryUUID) {
      uri.addQuery('repository_uuid', repositoryUUID);
    }

    if (dateRange) {
      uri.addQuery('date_range', dateRange);
    }

    return uri.toString();
  }
}
